/**
 * LeadCaptureModal Component
 * Modal de captura de dados do lead com campos simplificados e aceite de termos LGPD
 * US-034: Simplificado para 3 campos (nome, email, area) + checkbox de termos
 * Campos cargo, tamanhoEmpresa, dores serao coletados conversacionalmente no chat
 */

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSessionStore } from '@/features/chat/stores/sessionStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { TermsModal } from './TermsModal';

// =============================================================================
// CONSTANTS
// =============================================================================

export const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'mail.com',
  'uol.com.br',
  'bol.com.br',
  'terra.com.br',
  'ig.com.br',
  'globo.com',
  'zipmail.com.br',
];

export const CARGO_OPTIONS = [
  { label: 'CEO/Co-founder', value: 'CEO/Co-founder' },
  { label: 'Fundador(a)', value: 'So Fundador' },
  { label: 'C-Level', value: 'C-Level' },
  { label: 'VP/Diretor', value: 'VP/Diretor' },
  { label: 'Head', value: 'Head' },
  { label: 'Gerente/Head', value: 'Gerente/Head' },
  { label: 'Coordenador(a)', value: 'Coordenador(a)' },
  { label: 'Supervisor(a)/Coordenador(a)', value: 'Supervisor(a)/coordenador(a)' },
  { label: 'Lead', value: 'Lead' },
  { label: 'Analista', value: 'Analista' },
  { label: 'Estagiário(a)', value: 'Estagiario(a)' },
  { label: 'Outros', value: 'Outros' },
  { label: 'Superintendente', value: 'Superintendente' },
] as const;

export const AREA_OPTIONS = [
  { label: 'Tecnologia', value: 'Tecnologia' },
  { label: 'Produto', value: 'Produto' },
  { label: 'Design', value: 'Design' },
  { label: 'Tecnologia e Produto', value: 'Tecnologia' },
  { label: 'Dados', value: 'Dados' },
  { label: 'Inovação', value: 'Inova' },
  { label: 'Projetos', value: 'Projetos' },
  { label: 'InfoSec', value: 'InfoSec' },
  { label: 'Negócios', value: 'Neg' },
  { label: 'Engenharia', value: 'Engenharia' },
  { label: 'IA', value: 'IA' },
  { label: 'Outro(a)', value: 'Outro' },
] as const;

export const TAMANHO_OPTIONS = [
  { label: 'Até 10 pessoas', value: 'Até 10 pessoas' },
  { label: 'De 11 a 50', value: 'De 11 a 50' },
  { label: 'De 51 a 100', value: 'de 51 a 100' },
  { label: 'De 101 a 200', value: 'De 51 a 200' },
  { label: 'De 201 a 500', value: 'De 201 a 500' },
  { label: 'De 501 a 1000', value: 'De 501 a 1000' },
  { label: 'De 1001 a 5000', value: 'De 1001 a 5000' },
  { label: 'De 2.001 a 5.000', value: 'De 2.001 a 5.000' },
  { label: 'Mais de 5001', value: 'mais de 5001' },
] as const;

export const DORES_OPTIONS = [
  { label: 'Produtividade do meu time abaixo do esperado', value: 'dor_produtividade' },
  { label: 'Falta de capacity para entregar as demandas esperadas', value: 'dor_capacity' },
  {
    label: 'Falta de conhecimento técnico para entregar as demandas esperadas',
    value: 'Dor_conhecimento_tecnico',
  },
  { label: 'Nenhum desses desafios', value: 'Nenhum desses desafios no momento' },
] as const;

// =============================================================================
// SCHEMA
// =============================================================================

export const leadSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: 'Nome deve ter pelo menos 2 caracteres' })
    .max(100, { message: 'Nome deve ter no máximo 100 caracteres' }),
  email: z
    .string()
    .trim()
    .email({ message: 'E-mail inválido' })
    .max(255, { message: 'E-mail deve ter no máximo 255 caracteres' })
    .refine(
      (email) => {
        const domain = email.split('@')[1]?.toLowerCase();
        return domain !== undefined && !BLOCKED_EMAIL_DOMAINS.includes(domain);
      },
      { message: 'Por favor, utilize seu e-mail corporativo' }
    ),
  area: z.string().min(1, { message: 'Selecione sua área' }),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Você deve aceitar os termos para continuar',
  }),
});

export type LeadFormData = z.infer<typeof leadSchema>;

// =============================================================================
// COMPONENT
// =============================================================================

interface LeadCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeadCaptureModal({ open, onOpenChange }: LeadCaptureModalProps) {
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Session store actions to persist user data for /generate endpoint
  const setUserField = useSessionStore((state) => state.setUserField);
  const setConsent = useSessionStore((state) => state.setConsent);

  const form = useForm<LeadFormData>({
    // biome-ignore lint/suspicious/noExplicitAny: Zod v4 type incompatibility with @hookform/resolvers
    resolver: zodResolver(leadSchema as any),
    defaultValues: {
      name: '',
      email: '',
      area: '',
      acceptTerms: false,
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);

    const trimmedName = data.name.trim();
    const trimmedEmail = data.email.trim().toLowerCase();

    // Store lead data in localStorage for backwards compatibility
    const leadData = {
      name: trimmedName,
      email: trimmedEmail,
      area: data.area,
      acceptedTermsAt: new Date().toISOString(),
    };
    localStorage.setItem('leadData', JSON.stringify(leadData));

    // Update session store so data is available for /generate endpoint
    setUserField('name', trimmedName);
    setUserField('email', trimmedEmail);
    setUserField('area', data.area);

    // Use email domain as company fallback
    const emailDomain = trimmedEmail.split('@')[1];
    if (emailDomain) {
      setUserField('company', emailDomain);
    }

    setConsent(true);

    // Fire-and-forget POST to backend for HubSpot sync (non-blocking with 3s timeout)
    const sessionId = useSessionStore.getState().session.sessionId;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    fetch(`${import.meta.env.VITE_API_URL}/api/contacts/register`, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        name: trimmedName,
        email: trimmedEmail,
        area: data.area,
      }),
    })
      .catch(() => {
        // Silently ignore errors - do not block user flow
        // Errors are already logged on the backend side
      })
      .finally(() => clearTimeout(timeoutId));

    // Navigate immediately - don't wait for the POST response
    setIsSubmitting(false);
    onOpenChange(false);
    navigate('/assessment/selection');
  };

  const handleOpenTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowTerms(true);
  };

  return (
    <>
      <Dialog open={open && !showTerms} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Antes de começar</DialogTitle>
            <DialogDescription>
              Preencha seus dados para iniciar o diagnóstico e receber os resultados.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Seu nome" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail corporativo</FormLabel>
                    <FormControl>
                      <Input placeholder="seu@empresa.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Área</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione sua área" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AREA_OPTIONS.map((option) => (
                          <SelectItem key={option.label} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/30">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        Li e aceito os{' '}
                        <button
                          type="button"
                          onClick={handleOpenTerms}
                          className="text-primary underline hover:text-primary/80 font-medium"
                        >
                          Termos de Uso e Política de Privacidade
                        </button>
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full group"
                disabled={!form.formState.isValid || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    Enviando...
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  </>
                ) : (
                  <>
                    Iniciar diagnóstico
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <TermsModal open={showTerms} onOpenChange={setShowTerms} />
    </>
  );
}
